import { Head, Link, router, usePage } from "@inertiajs/react";
import {
  AlertTriangle,
  Building,
  Calendar,
  CheckCircle2,
  Trash2,
  User,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import type { AsyncOption } from "@/components/async-combobox";
import CommentsThread from "@/components/comments-thread";
import {
  EditableDataTable,
  type EditableDataTableAction,
} from "@/components/editable-data-table";
import InlineEditableAsyncCombobox from "@/components/inline-editable-async-combobox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { GROUPS } from "@/constants/groups";
import { useAutoRefreshOnProcessing } from "@/hooks/use-auto-refresh-on-processing";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import {
  organizationDeliveryNoteItemPath,
  organizationDeliveryNoteItemsPath,
  organizationDeliveryNotePath,
  organizationDeliveryNotesPath,
} from "@/routes";
import type {
  BreadcrumbItem,
  Comment,
  DeliveryNote,
  DeliveryNoteItem,
  SharedData,
} from "@/types";
import { formatDateAR } from "@/utils/date";

import AttachmentsSection from "./components/attachments-section";
import DeliveryNoteHeader from "./components/delivery-note-header";
import MarkAsReviewedDialog from "./components/mark-as-reviewed-dialog";
import MatchAICard from "./components/match-ai-card";
import PurchaseRequestChangeDialog from "./components/purchase-request-change-dialog";
import PurchaseRequestSection from "./components/purchase-request-section";
import { createItemColumns } from "./item-columns";

interface Props {
  delivery_note: DeliveryNote & {
    can_delete?: boolean;
  };
  items: DeliveryNoteItem[];
  comments?: Comment[];
}

const statusConfig: Record<
  string,
  { label: string; variant: "outline" | "default" | "secondary" }
> = {
  uploaded: { label: "Cargado", variant: "outline" },
  processing: { label: "Procesando", variant: "secondary" },
  pending_review: { label: "Pendiente de revisión", variant: "outline" },
  assigned: { label: "Asignado", variant: "default" },
  error: { label: "Error", variant: "default" },
};

function usePurchaseRequestChangePrompt(
  hasMatchedItems: boolean,
  currentPurchaseRequestId: number | null,
) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const resolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const requestChange = useCallback(
    (nextId: number | null) => {
      if (hasMatchedItems && nextId !== currentPurchaseRequestId) {
        return new Promise<boolean>((resolve) => {
          if (resolverRef.current) {
            resolverRef.current(false);
          }
          resolverRef.current = resolve;
          setIsDialogOpen(true);
        });
      }

      return Promise.resolve(true);
    },
    [currentPurchaseRequestId, hasMatchedItems],
  );

  const resolveAndReset = useCallback((result: boolean) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    resolveAndReset(true);
    setIsDialogOpen(false);
  }, [resolveAndReset]);

  const handleCancel = useCallback(() => {
    resolveAndReset(false);
    setIsDialogOpen(false);
  }, [resolveAndReset]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsDialogOpen(open);
      if (!open) {
        resolveAndReset(false);
      }
    },
    [resolveAndReset],
  );

  useEffect(
    () => () => {
      resolveAndReset(false);
    },
    [resolveAndReset],
  );

  return {
    requestChange,
    dialogProps: {
      open: isDialogOpen,
      onOpenChange: handleOpenChange,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
}

export default function Show({ delivery_note, items, comments }: Props) {
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<number | null>(null);
  const [showMarkReviewedDialog, setShowMarkReviewedDialog] = useState(false);

  // Get current user groups
  const shared = usePage().props as unknown as SharedData;
  const userGroups = shared.auth?.groups ?? [];
  const routes = useOrganizationRoutes();
  const organizationSlug = routes.organizationSlug;
  const deliveryNotesHref = organizationDeliveryNotesPath(organizationSlug);
  const deliveryNoteHref = organizationDeliveryNotePath(
    organizationSlug,
    delivery_note.id,
  );
  const uploadNewInvoicesHref = routes.invoices.uploadNew();
  const supplierSearchEndpoint = routes.suppliers.search();
  const commentsHref = routes.deliveryNotes.comments(delivery_note.id);
  const markReviewedHref = routes.deliveryNotes.markAsReviewed(
    delivery_note.id,
  );

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Remitos",
      href: deliveryNotesHref,
    },
    {
      title: delivery_note.number ?? `#${delivery_note.id}`,
      href: deliveryNoteHref,
    },
  ];

  // Check user roles
  const isSupervisor = userGroups.includes(GROUPS.supervisor);
  const isBuyer = userGroups.includes(GROUPS.buyer);
  const isRequester = userGroups.includes(GROUPS.requester);

  // Editing is not allowed while processing, assigned or after review (assumed assigned or later)
  const isEditableStatus = !["processing", "assigned"].includes(
    delivery_note.status,
  );

  // General edits (description, add/delete rows)
  const canEditItems = isSupervisor && isEditableStatus;
  const canUpdate = delivery_note.can_update ?? false;
  const hasMatchedItems = items.some((item) => item.request_item);
  const hasUnassignedItems = items.some((item) => !item.request_item);
  const { requestChange: handleBeforePRChange, dialogProps: prChangeDialog } =
    usePurchaseRequestChangePrompt(
      hasMatchedItems,
      delivery_note.purchase_request?.id ?? null,
    );

  // Quantity permissions
  // - Cant. Remito: supervisor or buyer
  // - Cant. Recibida: supervisor, buyer o requester
  const canEditNoteQuantity = isEditableStatus && (isSupervisor || isBuyer);
  const canEditReceivedQuantity =
    isEditableStatus && (isSupervisor || isBuyer || isRequester);

  // Otros campos de fila
  // - Unidad e Ítem de Solicitud: supervisor o buyer
  const canEditUnit = isEditableStatus && (isSupervisor || isBuyer);
  const canEditRequestItem = isEditableStatus && (isSupervisor || isBuyer);

  // Calculate items with differences between received quantity and quantity in note
  const itemsWithDifferences = items.filter((item) => {
    const received = Number(item.quantity_received ?? 0);
    const inNote = Number(item.quantity_in_note ?? 0);
    return received !== inNote && inNote > 0;
  });
  const hasDifferences = itemsWithDifferences.length > 0;

  // Calculate items without request_item (unmatched)
  const itemsWithoutMatch = items.filter((item) => !item.request_item).length;
  const hasUnmatchedItems = itemsWithoutMatch > 0;
  const showProcessingAlert = delivery_note.status === "processing";
  const showMatchCard = !showProcessingAlert;
  const showDifferencesAlert =
    delivery_note.status === "pending_review" && hasDifferences;
  const hasAttachments =
    Boolean(delivery_note.processed_pdf) ||
    delivery_note.remit_files.length > 0 ||
    delivery_note.evidence_files.length > 0;

  const defaultUnit = items[0]?.unit ?? "UN";
  const unitOptions = shared.enums?.units ?? [
    { label: "UN - Unidades", value: "UN" },
  ];

  const itemColumns = createItemColumns(
    canEditItems,
    delivery_note.available_request_items,
    canEditNoteQuantity,
    canEditReceivedQuantity,
    canEditUnit,
    canEditRequestItem,
    delivery_note.status,
    unitOptions,
  );

  // Auto-refresh page while processing
  useAutoRefreshOnProcessing(delivery_note.status);

  const handleCellCommit = useCallback(
    ({
      rowId,
      columnId,
      value,
    }: {
      rowId: string | number;
      columnId: string;
      value: unknown;
      row: DeliveryNoteItem;
    }) => {
      // Determine if the user can edit this specific column
      let canEditThisColumn = false;
      if (columnId === "quantity_in_note") {
        canEditThisColumn = canEditNoteQuantity;
      } else if (columnId === "quantity_received") {
        canEditThisColumn = canEditReceivedQuantity;
      } else if (columnId === "unit") {
        canEditThisColumn = canEditUnit;
      } else if (columnId === "request_item_id") {
        canEditThisColumn = canEditRequestItem;
      } else {
        // Fallback for any other editable columns
        canEditThisColumn = canEditItems;
      }

      if (!canEditThisColumn) {
        return Promise.resolve();
      }

      const numericField =
        columnId === "quantity_in_note" || columnId === "quantity_received";

      const payload: Record<string, string | number | null> = {};

      if (columnId === "request_item_id") {
        const option = value as AsyncOption | null;
        payload.request_item_id = option ? Number(option.value) : null;
      } else if (columnId === "unit") {
        const option = value as AsyncOption | null;
        payload.unit = option ? String(option.value) : null;
      } else if (numericField) {
        const raw = String(
          typeof value === "string" || typeof value === "number" ? value : "",
        )
          .replace(",", ".")
          .trim();
        payload[columnId] = raw === "" ? "" : Number(raw);
      } else {
        payload[columnId] = value as string | number | null;
      }

      const id = Number(rowId);

      return new Promise<void>((resolve, reject) => {
        router.patch(
          organizationDeliveryNoteItemPath(organizationSlug, id),
          { delivery_note_item: payload },
          {
            preserveScroll: true,
            preserveState: true,
            only: ["items"],
            onSuccess: () => {
              setCellErrors((prev) => {
                const next = { ...prev };
                delete next[`${rowId}:${columnId}`];
                return next;
              });
              resolve();
            },
            onError: (errors) => {
              const fieldKey =
                columnId === "request_item_id" ? "request_item" : columnId;
              const message =
                (errors[fieldKey] as string | undefined) ??
                (Object.values(errors)[0] as string | undefined) ??
                "No se pudo guardar el cambio";
              setCellErrors((prev) => ({
                ...prev,
                [`${rowId}:${columnId}`]: message,
              }));
              reject(new Error(message));
            },
          },
        );
      });
    },
    [
      canEditItems,
      canEditNoteQuantity,
      canEditReceivedQuantity,
      canEditUnit,
      canEditRequestItem,
      organizationSlug,
    ],
  );

  const handleAddRow = useCallback(() => {
    if (!canEditItems) return;

    setIsAddingRow(true);
    router.post(
      organizationDeliveryNoteItemsPath(organizationSlug),
      {
        delivery_note_item: {
          delivery_note_id: delivery_note.id,
          description_extracted: "Nuevo ítem",
          quantity_in_note: 1,
          quantity_received: 1,
          unit: defaultUnit,
        },
      },
      {
        preserveScroll: true,
        preserveState: true,
        only: ["items"],
        onSuccess: () => {
          setCellErrors({});
        },
        onError: (errors) => {
          const message =
            (Object.values(errors)[0] as string | undefined) ??
            "No se pudo agregar el ítem";
          window.alert(message);
        },
        onFinish: () => {
          setIsAddingRow(false);
        },
      },
    );
  }, [canEditItems, delivery_note.id, defaultUnit, organizationSlug]);

  const handleDeleteItem = useCallback(
    (item: DeliveryNoteItem) => {
      if (!canEditItems) {
        return Promise.resolve();
      }

      const id = Number(item.id);
      setDeletingItemId(id);

      return new Promise<void>((resolve, reject) => {
        router.delete(organizationDeliveryNoteItemPath(organizationSlug, id), {
          preserveScroll: true,
          preserveState: true,
          only: ["items"],
          onSuccess: () => {
            setCellErrors((prev) => {
              const next = { ...prev };
              Object.keys(next).forEach((key) => {
                if (key.startsWith(`${id}:`)) {
                  delete next[key];
                }
              });
              return next;
            });
            toast.success("Ítem eliminado");
            resolve();
          },
          onError: (errors) => {
            const message =
              (Object.values(errors)[0] as string | undefined) ??
              "No se pudo eliminar el ítem";
            toast.error(message);
            reject(new Error(message));
          },
          onFinish: () => {
            setDeletingItemId(null);
          },
        });
      });
    },
    [canEditItems, organizationSlug],
  );

  const itemActions: EditableDataTableAction<DeliveryNoteItem>[] = canEditItems
    ? [
        {
          id: "delete",
          label: "Eliminar",
          icon: Trash2,
          variant: "destructive",
          confirm: {
            title: "Eliminar ítem",
            description: "Esta acción no se puede deshacer.",
            confirmLabel: "Eliminar",
            cancelLabel: "Cancelar",
          },
          onSelect: (row: DeliveryNoteItem) => handleDeleteItem(row),
          isDisabled: (row: DeliveryNoteItem) => deletingItemId === row.id,
        },
      ]
    : [];

  const status = statusConfig[delivery_note.status];

  const handleMarkAsReviewed = useCallback(() => {
    router.post(
      markReviewedHref,
      {},
      {
        preserveScroll: true,
        onSuccess: () => setShowMarkReviewedDialog(false),
      },
    );
  }, [markReviewedHref]);

  const handleDeleteDeliveryNote = useCallback(() => {
    if (
      !window.confirm(
        "¿Estás seguro de que deseas eliminar este remito? Esta acción no se puede deshacer y eliminará todos los ítems y archivos asociados.",
      )
    ) {
      return;
    }

    router.delete(deliveryNoteHref, {
      onError: () => toast.error("Error al eliminar el remito"),
    });
  }, [deliveryNoteHref]);

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Remito ${delivery_note.number ?? delivery_note.id}`} />

      <div className="mx-auto w-full max-w-5xl">
        <div className="flex flex-col gap-8 p-8">
          {/* Header */}
          <DeliveryNoteHeader deliveryNote={delivery_note} status={status} />

          {showProcessingAlert && (
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
              Procesando tu remito… Esta página se actualizará automáticamente.
            </div>
          )}

          <Separator />

          {/* Info Grid */}
          <div className="grid gap-6 md:grid-cols-3">
            {delivery_note.purchase_request?.sector && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <Building className="h-3.5 w-3.5" />
                  Obra/Sector
                </div>
                <p className="text-base font-medium">
                  {delivery_note.purchase_request.sector.name}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <InlineEditableAsyncCombobox
                label="Proveedor"
                value={delivery_note.supplier?.id}
                field="supplier_id"
                updateUrl={deliveryNoteHref}
                icon={User}
                formatValue={() =>
                  delivery_note.supplier?.name ?? "Proveedor por asignar"
                }
                searchEndpoint={supplierSearchEndpoint}
                disabled={!canUpdate}
                refreshKeys={["delivery_note", "items"]}
              />
              {delivery_note.tax_id_extracted && (
                <p className="text-xs text-muted-foreground">
                  CUIT: {delivery_note.tax_id_extracted}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                Fecha del Remito
              </div>
              <p className="text-base font-medium">
                {delivery_note.note_date
                  ? formatDateAR(delivery_note.note_date)
                  : "-"}
              </p>
            </div>
          </div>

          <Separator />

          {/* Purchase Request */}
          {/* Discreet note: compra directa must be done from an invoice */}
          {!delivery_note.has_purchase_request && (
            <p className="text-sm text-muted-foreground">
              Compra directa: se realiza desde una factura. Primero{" "}
              <Link
                href={uploadNewInvoicesHref}
                className="underline hover:text-foreground"
              >
                subí la factura
              </Link>{" "}
              para continuar.
            </p>
          )}

          <PurchaseRequestSection
            deliveryNote={delivery_note}
            disabled={!canUpdate}
            onBeforeChange={handleBeforePRChange}
          />

          {/* Observations */}
          {delivery_note.observations && (
            <>
              <Separator />
              <div className="space-y-3">
                <h2 className="text-base font-medium">Observaciones</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed break-words max-w-prose">
                  {delivery_note.observations}
                </p>
              </div>
            </>
          )}

          {/* Intelligent Item Matching */}
          {showMatchCard ? (
            <>
              <Separator />
              <MatchAICard
                deliveryNoteId={delivery_note.id}
                hasPurchaseRequest={Boolean(delivery_note.has_purchase_request)}
                isProcessing={showProcessingAlert}
                hasUnassignedItems={hasUnassignedItems}
              />
              <Separator />
            </>
          ) : (
            <Separator />
          )}

          {/* Differences alert banner (only in pending_review) */}
          {showDifferencesAlert && (
            <Alert variant="default" className="border-amber-200 bg-amber-50">
              <AlertTriangle className="text-amber-600" />
              <AlertTitle className="text-amber-900">
                Diferencias detectadas
              </AlertTitle>
              <AlertDescription className="text-amber-700">
                Hay {itemsWithDifferences.length} ítem(s) con diferencias entre
                lo recibido y lo que figura en el remito. Revisa las cantidades
                antes de marcar como revisado.
              </AlertDescription>
            </Alert>
          )}

          {/* Items */}
          <div className="space-y-4">
            <div className="space-y-1">
              <h2 className="text-lg font-medium">Items del Remito</h2>
              <p className="text-sm text-muted-foreground">
                Productos extraídos del remito con cantidades
              </p>
            </div>
            <EditableDataTable
              columns={itemColumns}
              rows={items}
              getRowId={(item) => item.id}
              onCommit={handleCellCommit}
              onCommitSuccess={({ columnId }) => {
                const headerNode = itemColumns.find(
                  (column) => column.id === columnId,
                )?.header;
                const label =
                  typeof headerNode === "string" ? headerNode : "Campo";
                toast.success(`${label} actualizado`);
              }}
              errors={cellErrors}
              onAddRow={canEditItems ? handleAddRow : undefined}
              addRowLabel="Agregar ítem"
              rowActions={itemActions}
              actionsHeader="Acciones"
              isBusy={isAddingRow || deletingItemId !== null}
              emptyMessage="No hay items en este remito"
            />
          </div>

          <Separator />

          {/* Mark as Reviewed Action */}
          {delivery_note.can_be_marked_as_reviewed && (
            <div className="flex justify-end">
              <Button
                variant="default"
                size="default"
                onClick={() => setShowMarkReviewedDialog(true)}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Marcar como revisado
              </Button>
            </div>
          )}

          {/* Attachments */}
          {hasAttachments && (
            <>
              <Separator />
              <AttachmentsSection
                processedPdf={delivery_note.processed_pdf}
                remitFiles={delivery_note.remit_files}
                evidenceFiles={delivery_note.evidence_files}
                analysisStatus={
                  delivery_note.evidence_analysis_status as string | undefined
                }
                analysisResults={delivery_note.evidence_analysis_results}
                onRefresh={() => router.reload({ only: ["delivery_note"] })}
              />
            </>
          )}

          <Separator />

          <CommentsThread
            comments={comments ?? []}
            commentsPath={commentsHref}
            resourceLabel="el remito"
          />

          <Separator />

          {/* Actions */}
          <div className="flex justify-between items-center">
            {delivery_note.can_delete ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteDeliveryNote}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar remito
              </Button>
            ) : (
              <div /> /* Spacer */
            )}
            <Button variant="ghost" size="sm" asChild>
              <Link href={deliveryNotesHref}>Volver a listado</Link>
            </Button>
          </div>
        </div>
      </div>

      <PurchaseRequestChangeDialog
        open={prChangeDialog.open}
        onOpenChange={prChangeDialog.onOpenChange}
        onConfirm={prChangeDialog.onConfirm}
        onCancel={prChangeDialog.onCancel}
      />

      <MarkAsReviewedDialog
        open={showMarkReviewedDialog}
        onOpenChange={setShowMarkReviewedDialog}
        deliveryNote={delivery_note}
        hasUnmatchedItems={hasUnmatchedItems}
        unmatchedCount={itemsWithoutMatch}
        hasDifferences={hasDifferences}
        differencesCount={itemsWithDifferences.length}
        onConfirm={handleMarkAsReviewed}
      />
    </AppLayout>
  );
}
